import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { mumsApi } from "./mumsApi";
import { Search, Database, Loader2, Sprout, Flower2, Flower, Leaf } from "lucide-react";

const KINDS = [
  { v: "", label: "All" },
  { v: "tuber", label: "Tubers" },
  { v: "bloom", label: "Blooms" },
  { v: "flower", label: "Flowers" },
  { v: "seed", label: "Seeds" },
];
const ICON = { tuber: Sprout, bloom: Flower2, flower: Flower, seed: Leaf };
const ROUTE = { tuber: "/mums-shed/tubers", bloom: "/mums-shed/blooms", flower: "/mums-shed/flowers", seed: "/mums-shed/seeds" };

export default function MumsDatabase() {
  const nav = useNavigate();
  const [data, setData] = useState({ count: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      const run = async () => {
        let res;
        try {
          res = await mumsApi.database(search.trim() || undefined, kind || undefined);
        } catch {
          res = null;
        }
        if (cancelled) return;
        if (res) setData(res);
        setLoading(false);
      };
      run();
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search, kind]);

  const grouped = useMemo(() => {
    // Group alphabetically by first letter
    const m = new Map();
    for (const i of data.items) {
      const k = (i.name || "?").charAt(0).toUpperCase();
      m.set(k, [...(m.get(k) || []), i]);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [data.items]);

  return (
    <div className="space-y-6" data-testid="mums-database-page">
      <header>
        <div className="mum-chip"><Database size={12} /> Database</div>
        <h1 className="font-display text-4xl mt-2">The Alphabetical Database</h1>
        <p className="font-display italic text-[var(--mum-cobalt)] text-sm mt-1">Every variety from tubers, blooms, flowers and seeds — in one place.</p>
      </header>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--mum-cobalt)]/60" />
          <input className="mum-field pl-9" placeholder="Search across the whole garden…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="mums-db-search" />
        </div>
        <div className="flex gap-2 flex-wrap" data-testid="mums-db-filter">
          {KINDS.map((b) => (
            <button key={b.v || "all"} type="button" className={`mum-tab ${kind === b.v ? "is-active" : ""}`} onClick={() => setKind(b.v)} data-testid={`db-filter-${b.v || "all"}`}>{b.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 size={24} className="animate-spin mx-auto text-[var(--mum-cobalt)]" /></div>
      ) : data.items.length === 0 ? (
        <div className="mums-card-quiet p-10 text-center" data-testid="mums-db-empty">
          <Database size={36} className="text-[var(--mum-cobalt)]/60 mx-auto" />
          <p className="font-display italic text-[var(--mum-cobalt)] text-lg mt-3">Nothing recorded yet.</p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="mums-db-list">
          {grouped.map(([letter, list]) => (
            <section key={letter}>
              <h2 className="font-display text-3xl text-[var(--mum-sapphire)] border-b border-[var(--mum-cornflower)] pb-1 mb-3">{letter}</h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {list.map((i) => {
                  const Icon = ICON[i.kind] || Sprout;
                  return (
                    <li key={`${i.kind}-${i.id}`} className="mums-card p-3 flex items-center gap-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => nav(ROUTE[i.kind])} data-testid={`db-row-${i.id}`}>
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-[var(--mum-sky)]/50 flex items-center justify-center shrink-0">
                        {i.photo ? <img src={i.photo} alt={i.name} className="w-full h-full object-cover" /> : <Icon size={18} className="text-[var(--mum-cobalt)]/70" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-display text-lg text-[var(--mum-sapphire)] truncate">{i.name}</h3>
                          <span className="text-[0.6rem] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--mum-sky)] text-[var(--mum-sapphire)]">{i.kind}</span>
                        </div>
                        {i.subtitle && <p className="text-xs text-[var(--mum-cobalt)]/75 truncate">{i.subtitle}</p>}
                      </div>
                      {i.status && <span className="text-[0.65rem] uppercase tracking-wider text-[var(--mum-cobalt)]/70 shrink-0">{i.status}</span>}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
